/**
 * Changes service — undo a changeset. A revert is another changeset: it moves
 * forward by writing the opposite of each unit, and skips (and reports) any
 * unit that something else has touched since. `revert` applies it and returns
 * the changeset it opened; `impact` is the cheap bound to show first. The
 * rules are in postgres/docs/CHANGESETS.md.
 */

import { fetchChangesetImpact, undoChangeset } from "../gql/changes.js"
import type { Mutate, Query } from "../index.js"

export function createChangesService({ query, mutate }: { query: Query; mutate: Mutate }) {
  /** Revert the changeset. Returns the changeset the revert opened, whose
   *  `context` has `reverted`, `skipped` and `skipped_units` (entity, field,
   *  outcome and reason of the first refusals), or null when every unit was
   *  already undone and there was nothing to report. */
  const revert = async (changesetId: number) => {
    const data = await mutate(undoChangeset, { changesetId })
    return data.undo_changeset?.[0] ?? null
  }

  /** How much the changeset touched: its rows and the distinct records they
   *  name. A cheap bound, no plan. */
  const impact = async (changesetId: number): Promise<{ changes: number; records: number }> => {
    const data = await query(fetchChangesetImpact, { changesetId })
    const aggregate = data.record_changes_aggregate.aggregate
    return { changes: aggregate?.changes ?? 0, records: aggregate?.records ?? 0 }
  }

  return { revert, impact }
}
